'use client';

import Link from 'next/link';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * Brand-consistent full-page feedback used by the app-wide error and
 * not-found boundaries. Colors come from the theme CSS variables so it looks
 * correct in both light and dark modes.
 *
 * Must stay a client component: it hands `component={Link}` to the MUI Button,
 * and a server component would try to serialize that function prop across the
 * RSC boundary, crashing the render (HTTP 500 on every 404).
 */
export const FullPageMessage = ({
  badge,
  title,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryAction,
}: {
  badge?: string;
  title: string;
  description: string;
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryAction?: () => void;
}) => (
  <Container
    maxWidth='sm'
    sx={{
      minHeight: '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
    }}
  >
    <Stack spacing={2} alignItems='center' textAlign='center'>
      {badge ? (
        <Typography
          variant='overline'
          sx={{
            color: 'hsl(var(--primary))',
            letterSpacing: '0.2em',
            fontWeight: 700,
          }}
        >
          {badge}
        </Typography>
      ) : null}
      <Typography
        variant='h1'
        sx={{
          fontFamily: 'var(--font-display), serif',
          fontSize: { xs: '2.2rem', sm: '3rem' },
          lineHeight: 1.15,
          color: 'hsl(var(--foreground))',
        }}
      >
        {title}
      </Typography>
      <Typography variant='body1' sx={{ color: 'hsl(var(--muted-foreground))', maxWidth: 460 }}>
        {description}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
        {primaryLabel && primaryHref ? (
          <Button
            component={Link}
            href={primaryHref}
            variant='contained'
            disableElevation
            sx={{ borderRadius: 999, px: 3, py: 1.1, textTransform: 'none', fontWeight: 700 }}
          >
            {primaryLabel}
          </Button>
        ) : null}
        {secondaryLabel && secondaryAction ? (
          <Button
            onClick={secondaryAction}
            variant='outlined'
            sx={{ borderRadius: 999, px: 3, py: 1.1, textTransform: 'none', fontWeight: 700 }}
          >
            {secondaryLabel}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  </Container>
);
